import { firstValueFrom } from 'rxjs';
import omit from 'lodash/omit';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DinteroCheckoutInstance } from '@dintero/checkout-web-sdk';
import { COUNTRY_CODE_LOCALE_CODE } from '@cakeiteasy/web-utils';

import { IWebhookUrls } from '../../models/i-payment-models';
import { PaymentApiService } from '../../../api-services/services/payment-api.service';
import { PAYMENT_PERSON_TYPES, PAYMENT_PLATFORM_TYPES } from '../../payment.constants';
import { DinteroCheckoutService, IDinteroPaymentSession } from '../../services/dintero-checkout.service';
import { PaymentStorageService } from '../../services/payment-storage.service';

@Component({
  standalone: false,
  selector: 'mobile-dintero-payment',
  templateUrl: './mobile-dintero-payment.component.html',
  styleUrls: ['../dintero-payment/dintero-payment.component.scss'],
})
export class MobileDinteroPaymentComponent implements AfterViewInit, OnChanges, OnDestroy {
  public personType = input<string>();
  public countryCode = input<string>();
  public webhookUrls = input<IWebhookUrls>();
  public queueProcessing = input<Promise<any>>();
  public giftCardCode = input<string>(null);
  public cancelPaymentOnClose = input(false);

  @ViewChild('embedContainer', { static: true }) embedContainer: ElementRef<HTMLDivElement>;

  public onSuccess = output<string>();
  public onFail = output<any>();
  public onError = output<any>();
  public onOrderCreate = output<string>();
  public sessionActivityChanged = output<boolean>();
  public onBusy = output<Promise<any>>();

  public checkout: DinteroCheckoutInstance;
  public loading: Promise<any>;

  private _paymentApi = inject(PaymentApiService);
  private _storage = inject(PaymentStorageService);
  private _dinteroCheckout = inject(DinteroCheckoutService);
  private _sessionStarted = false;
  private _destroyed = false;

  public ngAfterViewInit() {
    setTimeout(() => {
      if (!this._destroyed) {
        this.startSession();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!changes.queueProcessing || changes.queueProcessing.firstChange || !this._sessionStarted) {
      return;
    }

    this.startSession();
  }

  public ngOnDestroy() {
    this._destroyed = true;
    this._destroyCheckout();
  }

  public startSession() {
    const queueProcessing = this.queueProcessing() || Promise.resolve(null);
    const loading = queueProcessing.then(() => {
      this._destroyCheckout();
      return this._submit();
    });

    this.loading = loading;
    this.onBusy.emit(loading);
    return loading;
  }

  private _submit() {
    const contactInfo = this._storage.getContactInfo(PAYMENT_PLATFORM_TYPES.dintero) || {};
    let payload = { ...contactInfo, code: this.giftCardCode() };

    if (this.personType() === PAYMENT_PERSON_TYPES.PRIVATE) {
      payload = omit(payload, ['userCompanyName', 'invoiceCompanyId', 'invoiceCompanyOrgNumber']);
    }

    this._sessionStarted = true;

    return firstValueFrom(this._paymentApi.createDinteroPayment({
      ...payload,
      ...this.webhookUrls(),
    }))
      .then((data: IDinteroPaymentSession) => {
        this.onOrderCreate.emit(data.sid);
        this.sessionActivityChanged.emit(true);

        return this._dinteroCheckout.createInlineCheckout({
          snippedData: data,
          language: COUNTRY_CODE_LOCALE_CODE[this.countryCode()],
          onPayment: (event) => {
            this.checkout = null;
            this.sessionActivityChanged.emit(false);
            this.onSuccess.emit(event.href);
          },
          onError: (error) => {
            this.checkout = null;
            this.sessionActivityChanged.emit(false);
            this.onError.emit(error);
          },
          onCancel: () => {
            if (this.cancelPaymentOnClose() && data.paymentId) {
              firstValueFrom(this._paymentApi.cancelPayment(data.paymentId));
            }

            this.checkout = null;
            this.sessionActivityChanged.emit(false);
          },
          containerToEmbed: this.embedContainer.nativeElement,
        });
      })
      .then((checkout) => {
        this.checkout = checkout;
        return checkout;
      })
      .catch((error) => {
        this.onFail.emit(error);
        return null;
      });
  }

  private _destroyCheckout() {
    if (this.checkout) {
      this._dinteroCheckout.destroy(this.checkout);
      this.checkout = null;
      this.sessionActivityChanged.emit(false);
    }
  }
}
